var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var AssignmentOptimizationConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import './assignmentOptimizationConstraint'; // force import JSON schema
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { isSourceValid } from './validation/validateHelper';
import { assignmentOptimizationConfigSchemaValidation } from '../validations.generated';
export var OptimizationFunctions;
(function (OptimizationFunctions) {
    OptimizationFunctions["Balance"] = "balance";
    OptimizationFunctions["Minimize"] = "minimize";
    OptimizationFunctions["Maximize"] = "maximize";
})(OptimizationFunctions || (OptimizationFunctions = {}));
export var SolverType;
(function (SolverType) {
    SolverType["CP_SAT"] = "CP-SAT";
    SolverType["SCIP"] = "SCIP";
})(SolverType || (SolverType = {}));
export const assignmentOptimizationConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:AssignmentOptimizationConfig',
    type: 'object',
    properties: {
        itemsToAssignColumn: { type: 'string', minLength: 1 },
        assigneeColumn: { type: 'string', minLength: 1 },
        valuesToOptimizeColumn: { type: 'string', minLength: 1 },
        functionToOptimize: {
            type: 'string',
            enum: Object.values(OptimizationFunctions)
        },
        multipleValuesPerItems: { type: 'boolean' },
        mappingColumn: { type: 'string', minLength: 1 },
        constraints: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:AssignmentOptimizationConstraint' }
        },
        assignAllTasks: { type: 'boolean' },
        assignmentLockColumn: { type: 'string', minLength: 1 },
        groupingColumn: { type: 'string', minLength: 1 },
        maxTime: { type: 'number', minimum: 15, maximum: 86400 },
        solverType: {
            type: 'string',
            enum: Object.values(SolverType)
        }
    },
    required: [
        'itemsToAssignColumn',
        'assigneeColumn',
        'valuesToOptimizeColumn',
        'functionToOptimize',
        'multipleValuesPerItems',
        'assignAllTasks',
        'maxTime'
    ]
};
let AssignmentOptimizationConfig = AssignmentOptimizationConfig_1 = class AssignmentOptimizationConfig extends ToolConfig {
    constructor(itemsToAssignColumn, assigneeColumn, valuesToOptimizeColumn, functionToOptimize, multipleValuesPerItems, assignAllTasks, maxTime, constraints, mappingColumn, assignmentLockColumn, customFormats, roundToInt = false, groupingColumn) {
        super(ToolType.ASSIGNMENT_OPTIMIZATION, customFormats);
        this.itemsToAssignColumn = itemsToAssignColumn;
        this.assigneeColumn = assigneeColumn;
        this.valuesToOptimizeColumn = valuesToOptimizeColumn;
        this.functionToOptimize = functionToOptimize;
        this.multipleValuesPerItems = multipleValuesPerItems;
        this.assignAllTasks = assignAllTasks;
        this.maxTime = maxTime;
        this.solverType = roundToInt ? SolverType.CP_SAT : SolverType.SCIP;
        if (constraints !== undefined)
            this.constraints = constraints;
        if (mappingColumn !== undefined)
            this.mappingColumn = mappingColumn;
        if (groupingColumn !== undefined)
            this.groupingColumn = groupingColumn;
        if (assignmentLockColumn !== undefined)
            this.assignmentLockColumn = assignmentLockColumn;
        if (groupingColumn !== undefined)
            this.groupingColumn = groupingColumn;
        JSON.validate(this, AssignmentOptimizationConfig_1);
    }
    get items() {
        return AssignmentOptimizationConfig_1.items;
    }
    get assignees() {
        return AssignmentOptimizationConfig_1.assignees;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return AssignmentOptimizationConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return AssignmentOptimizationConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return AssignmentOptimizationConfig_1.outs;
    }
    static get items() {
        return AssignmentOptimizationConfig_1.ins[0];
    }
    static get assignees() {
        return AssignmentOptimizationConfig_1.ins[1];
    }
    static get out() {
        return AssignmentOptimizationConfig_1.outs[0];
    }
    static get ins() {
        return ['items', 'assignees'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.ASSIGNMENT_OPTIMIZATION.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.LEARN], this.ins, this.outs, [], false);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const items = ins.schemas.at(this.items);
        const assignees = ins.schemas.at(this.assignees);
        if (items && assignees) {
            const exp = tsb.export(this.out);
            const dependents = [];
            const itemsOutColumns = this.getItemsOutputColumns();
            const itemsColumnsSource = new Set();
            itemsOutColumns.forEach((col) => {
                itemsColumnsSource.add(exp.findSourceColumn(this.items, col));
            });
            dependents.push(...itemsColumnsSource);
            const assigneeColumnSource = exp.findSourceColumn(this.assignees, this.assigneeColumn);
            dependents.push(assigneeColumnSource);
            itemsColumnsSource.forEach((item) => {
                exp.copy(this.items, item, false, true);
            });
            exp.makeNew(new Column(`Assigned_${this.assigneeColumn}`, 'string'), dependents, true);
            exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    getItemsOutputColumns() {
        const colsOutput = new Set([this.itemsToAssignColumn, this.valuesToOptimizeColumn]);
        if (this.multipleValuesPerItems && this.mappingColumn !== undefined) {
            colsOutput.add(this.mappingColumn);
        }
        if (this.assignmentLockColumn !== undefined) {
            colsOutput.add(this.assignmentLockColumn);
        }
        if (this.constraints !== undefined) {
            this.constraints.forEach((item) => {
                colsOutput.add(item.valueColumn);
            });
        }
        if (this.groupingColumn !== undefined) {
            colsOutput.add(this.groupingColumn);
        }
        return colsOutput;
    }
    isValidForCalc(iConfigHelper, scope) {
        const items = scope.schemas.at(this.items);
        const assignees = scope.schemas.at(this.assignees);
        if (!items || !assignees) {
            return ToolValidationState.BAD_METADATA;
        }
        const itemsColumns = this.getItemsOutputColumns();
        const itemsInputs = new Map();
        const assigneeInputs = new Map();
        itemsColumns.forEach((col) => {
            if (col !== this.valuesToOptimizeColumn) {
                itemsInputs.set(col, ['string', 'number']);
            }
            else {
                itemsInputs.set(col, ['number']);
            }
        });
        assigneeInputs.set(this.assigneeColumn, ['string', 'number']);
        const outputs = [];
        outputs.push(`Assigned_${this.assigneeColumn}`);
        if (itemsInputs.has(`Assigned_${this.assigneeColumn}`)) {
            return ToolValidationState.BAD_METADATA;
        }
        if (!isSourceValid(items, itemsInputs, outputs)) {
            return ToolValidationState.BAD_METADATA;
        }
        if (!isSourceValid(assignees, assigneeInputs, outputs)) {
            return ToolValidationState.BAD_METADATA;
        }
        if (new Set(outputs).size !== outputs.length) {
            return ToolValidationState.BAD_METADATA;
        }
        if (this.valuesToOptimizeColumn === this.groupingColumn) {
            return ToolValidationState.INVALID;
        }
        return ToolValidationState.VALID;
    }
    isValidForRun(iConfigHelper, scope, pass) {
        return pass ? ToolValidationState.BAD_METADATA : this.isValidForCalc(iConfigHelper, scope);
    }
};
AssignmentOptimizationConfig = AssignmentOptimizationConfig_1 = __decorate([
    JsonSchemaV2(assignmentOptimizationConfigSchema, assignmentOptimizationConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.ASSIGNMENT_OPTIMIZATION)
], AssignmentOptimizationConfig);
export { AssignmentOptimizationConfig };
//# sourceMappingURL=assignmentOptimizationConfig.js.map